import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, EmptyState, Spinner, Money, StatCard } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PiggyBank, Plus, Trash2 } from "lucide-react";
import { fmtDate, fmtDateInput } from "@/lib/format";

const CATS = ["Materials", "Vehicle", "Insurance", "Equipment", "Subcontractor", "Office", "General"];

function ExpensesInner() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data, loading, refetch } = useFetch("/expenses");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ description: "", amount: 0, category: "Materials", vat_amount: 0, supplier: "", date: fmtDateInput(new Date()) });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.description.trim()) return toast.error("Description required");
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), vat_amount: Number(form.vat_amount), date: new Date(form.date).toISOString() });
      toast.success("Expense logged"); setOpen(false); refetch();
      setForm({ description: "", amount: 0, category: "Materials", vat_amount: 0, supplier: "", date: fmtDateInput(new Date()) });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/expenses/${id}`); refetch(); };

  const items = data?.items || [];
  const total = items.reduce((s, e) => s + e.amount, 0);
  const vat = items.reduce((s, e) => s + (e.vat_amount || 0), 0);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Expenses & Receipts" description="Log what you spend to run the business."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-expense-button"><Plus size={16} /> Log expense</Btn>} />
      <div className="grid grid-cols-2 gap-4 mb-4">
        <StatCard label="Total spent" value={<Money value={total} symbol={sym} />} tone="amber" />
        <StatCard label="VAT on purchases" value={<Money value={vat} symbol={sym} />} tone="blue" />
      </div>
      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={PiggyBank} title="No expenses yet" description="Log costs to track spending and VAT."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> Log expense</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Description</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Category</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Supplier</th><th className="px-4 py-3 font-semibold hidden md:table-cell">Date</th>
              <th className="px-4 py-3 font-semibold text-right">Amount</th><th></th></tr></thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{e.description}</td>
                  <td className="px-4 py-3 hidden sm:table-cell"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{e.category}</span></td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{e.supplier || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{fmtDate(e.date)}</td>
                  <td className="px-4 py-3 text-right"><Money value={e.amount} symbol={sym} /></td>
                  <td className="px-4 py-3 text-right"><button onClick={() => remove(e.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><label className="text-sm font-medium text-slate-700">Description *</label><input value={form.description} onChange={set("description")} data-testid="expense-field-description" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700">Amount</label><input type="number" value={form.amount} onChange={set("amount")} data-testid="expense-field-amount" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              <div><label className="text-sm font-medium text-slate-700">VAT amount</label><input type="number" value={form.vat_amount} onChange={set("vat_amount")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              <div><label className="text-sm font-medium text-slate-700">Category</label><select value={form.category} onChange={set("category")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><label className="text-sm font-medium text-slate-700">Date</label><input type="date" value={form.date} onChange={set("date")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700">Supplier</label><input value={form.supplier} onChange={set("supplier")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save} data-testid="expense-save-button">Save</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Expenses() { return <PlanGate feature="expenses"><ExpensesInner /></PlanGate>; }
